import { SimpleState } from 'router5/types/types/base';

import { toNormalizedRouteName } from '@neptune/shared/enterprise-context-util';
import {
  Entity,
  EntityType,
  fetchEntityChunk,
  getRunIdentificationPair,
} from '@neptune/shared/entity-domain';
import { makeEntityIdentifier } from '@neptune/shared/entity-util';

import { getIdFromAttributes, knownAttributes } from 'domain/experiment/attribute';

/**
 * Fetches entity for in-project display route.
 * WARN: Fetched entity will contain only well-known attributes.
 * @param requestedRoute
 */
export async function fetchEntityForInProjectDisplayRoute(
  requestedRoute: SimpleState,
): Promise<Entity> {
  const { organizationName, projectName, runIdentificationKey, type } = requestedRoute.params;

  return fetchEntityChunk({
    id: makeEntityIdentifier(organizationName, projectName, runIdentificationKey),
    fieldsToFetch: knownAttributes,
    ...(type ? { type } : { holderType: 'experiment' }),
  });
}

type InProjectEntityDisplayType = {
  type: Exclude<EntityType, 'project'> | 'experiment';
};

export function entityMatchesDisplayType(entity: Entity, { type }: InProjectEntityDisplayType) {
  return entity.type === type;
}

export function typeOfProjectEntityDisplayRoute(
  route: SimpleState,
): InProjectEntityDisplayType | undefined {
  const name = toNormalizedRouteName(route.name);
  const params = route.params;
  const type: string | undefined = params.type;

  if ('runIdentificationKey' in params) {
    if ((name === 'project.runs' || name === 'project.runs-tab') && params.tab === 'details') {
      return { type: 'run' };
    }

    if (name === 'project.trash' && (type === 'run' || type === 'experiment')) {
      return { type };
    }
  }

  return undefined;
}

export function getInProjectEntityDisplayRoute(entity: Entity): SimpleState | undefined {
  if (entity.type === 'project') {
    // We want to show the entity in context of the project, so it makes no sense for this entity to be a project.
    return undefined;
  }

  const pair = getRunIdentificationPair(entity);

  const type = pair?.experimentName ? 'experiment' : entity.type;
  const runIdentificationKey = pair?.experimentName || getIdFromAttributes(entity.attributes);

  const commonParams = {
    runIdentificationKey,
    projectName: entity.projectName,
    organizationName: entity.organizationName,
  };

  switch (type) {
    case 'run':
    case 'experiment':
      return {
        name: 'project.runs',
        params: { ...commonParams, tab: 'details', type },
      };
  }

  return undefined;
}
